
import VideoLayout from '../../../modules/UI/videolayout/VideoLayout.js';
import { CONFERENCE_WILL_LEAVE, IS_CUSTOM_PARTICIPANT_EVERYONE, IS_CUSTOM_PARTICIPANT_VISIBLE } from '../base/conference';
import { PICTURE_IN_PICTURE_MODE } from '../base/config/actionTypes.js';
import { MEDIA_TYPE } from '../base/media';
import { FAKE_PARTICPANT_JOINED, FAKE_PARTICPANT_LEFT } from '../base/mtApi/actionTypes.js';
import {
    DOMINANT_SPEAKER_CHANGED,
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    PARTICIPANT_UPDATED,
    PIN_PARTICIPANT,
    SET_MODERATOR_PIN,
    getLocalParticipant,
    getParticipantById
} from '../base/participants';
import { MiddlewareRegistry } from '../base/redux';
import { TRACK_ADDED, TRACK_REMOVED, TRACK_STOPPED, TRACK_UPDATED } from '../base/tracks';
import { handlePinParticipantFilmstrip } from '../conference/filmStripViewManager';
import { SET_DOCUMENT_EDITING_STATUS } from '../etherpad/actionTypes.js';
import { SET_FILMSTRIP_VISIBLE, SET_VISIBLE_PARTICIPANTS_START_INDEX, TILE_VIEW_TILE_LIMIT } from '../filmstrip';
import { SET_GENERICIFRAME_VISIBILITY_STATUS } from '../genericiframe/actionTypes.js';
import {
    PARTICIPANTS_PANE_CLOSE,
    PARTICIPANTS_PANE_OPEN
} from '../participants-pane/actionTypes.js';
import { SET_WHITEBOARD_INSTANCE } from '../toolbox/actionTypes.js';
import { LOCAL_VIDEO_ZOOM_CHANGED, REMOTE_VIDEO_ZOOM_CHANGED } from '../zoom-sync';

import './middleware.any';


/**
 * Determines whether a participant update affects thumbnail ordering.
 *
 * @param {Object} participantUpdate
 * @returns {boolean}
 */
function shouldSortForParticipantUpdate(participantUpdate = {}) {
    return [
        'dominantSpeaker',
        'pinned',
        'moderatorPin',
        'raisedHand',
        'raisedHandTimestamp',
        'role'
    ].some(key => Object.prototype.hasOwnProperty.call(participantUpdate, key));
}

/**
 * Determines whether a track update affects thumbnail ordering/visibility.
 *
 * @param {Object} trackUpdate
 * @returns {boolean}
 */
function shouldSortForTrackUpdate(trackUpdate = {}) {
    if (!trackUpdate) {
        return false;
    }

    if (Object.prototype.hasOwnProperty.call(trackUpdate, 'videoType')
        || Object.prototype.hasOwnProperty.call(trackUpdate, 'mediaType')) {
        return true;
    }

    if (Object.prototype.hasOwnProperty.call(trackUpdate, 'muted')) {
        const mhTrack = trackUpdate.mhTrack;

        return Boolean(
            mhTrack
            && typeof mhTrack.isVideoTrack === 'function'
            && mhTrack.isVideoTrack()
        );
    }

    return false;
}

/**
 * Middleware which intercepts actions and updates the legacy component
 * {@code VideoLayout} as needed. The purpose of this middleware is to redux-ify
 * {@code VideoLayout} without having to simultaneously react-ifying it.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */

MiddlewareRegistry.register(store => next => action => {
    // Purposefully perform additional actions after state update to mimic
    // being connected to the store for updates.
    const result = next(action);

    switch (action.type) {
    case CONFERENCE_WILL_LEAVE:
        VideoLayout.reset();
        break;

    case PARTICIPANT_JOINED:
        if (!action.participant.local) {
            VideoLayout.addRemoteParticipantContainer(
          getParticipantById(store.getState(), action.participant.id)
            );
        }
        break;

    case TRACK_STOPPED: {
        const { mhTrack } = action.track;

        if (mhTrack.isLocal()) {
            const participant = getLocalParticipant(store.getState());

            VideoLayout._updateLargeVideoIfDisplayed(participant?.id);
        }
        break;
    }

    case PARTICIPANT_LEFT:
        VideoLayout.removeParticipantContainer(action.participant.id);
        break;

    case FAKE_PARTICPANT_JOINED: {
        const { id, name, fakeParticipant, conference } = action;

        // Create containers for all fake participants including LocalScreenShare
        // VideoLayout.addScreenShareParticipantContainer will handle the routing

        VideoLayout.addScreenShareParticipantContainer(action, {
            fakeParticipant,
            conference,
            id,
            name
        });
        break;
    }

    case PARTICIPANT_UPDATED: {
        // Look for actions that triggered a change to connectionStatus. This is
        // done instead of changing the connection status change action to be
        // explicit in order to minimize changes to other code.
        if (typeof action.participant.connectionStatus !== 'undefined') {
            VideoLayout.onParticipantConnectionStatusChanged(
          action.participant.id,
          action.participant.connectionStatus
            );
        }

        if (shouldSortForParticipantUpdate(action.participant)) {
            VideoLayout.sortThumbnails();
        }

        break;
    }

    case FAKE_PARTICPANT_LEFT:
        VideoLayout.removeParticipantContainer(action.id, action.fakeParticipant);
        break;

    case SET_VISIBLE_PARTICIPANTS_START_INDEX:
    case DOMINANT_SPEAKER_CHANGED:
        VideoLayout.sortThumbnails();
        break;

    case PIN_PARTICIPANT: {
        // The middleware runs AFTER the reducer, so we check the final state
        const state = store.getState();
        const actionParticipantId = action.participant?.id;

        // Check if the participant is pinned AFTER the action has been processed
        // Handle local participant specially as it might have different ID handling
        let participant = null;

        if (actionParticipantId) {
            const localParticipant = getLocalParticipant(state);

            if (localParticipant && localParticipant.id === actionParticipantId) {
                participant = localParticipant;
            } else {
                participant = getParticipantById(state, actionParticipantId);
            }
        }

        const isNowPinned = participant?.pinned || false;

        // Pass the correct value to onPinChange: participant ID if pinned, null if unpinned
        VideoLayout.onPinChange(isNowPinned ? actionParticipantId : null);
        VideoLayout.sortThumbnails();
        break;
    }

    case TILE_VIEW_TILE_LIMIT: {
        VideoLayout.sortThumbnails();
        break;
    }

    case SET_MODERATOR_PIN: {
        // For moderator pin, check the action to determine if pinning or unpinning
        const actionParticipantId = action.participant?.id;
        const pinAction = action.participant?.action || 'pin';

        if (pinAction === 'pin' || pinAction === 'unpin') {
            const state = store.getState();

            handlePinParticipantFilmstrip(store.dispatch, state, 'SET_MODERATOR_PIN_middleware', pinAction === 'pin' ? action.participant : { id: null });
        }

        // Pass the correct value to onPinChange: participant ID if pinning, null if unpinning
        VideoLayout.onPinChange(pinAction === 'pin' ? actionParticipantId : null);
        break;
    }

    case PICTURE_IN_PICTURE_MODE: {
        VideoLayout.updateAllVideos();

        window.requestAnimationFrame(() => {
            VideoLayout.sortThumbnails();
        });
        break;
    }

    case PARTICIPANTS_PANE_CLOSE:
    case PARTICIPANTS_PANE_OPEN:
    case SET_FILMSTRIP_VISIBLE:
    case IS_CUSTOM_PARTICIPANT_EVERYONE:
    case IS_CUSTOM_PARTICIPANT_VISIBLE:
    case SET_GENERICIFRAME_VISIBILITY_STATUS:
    case SET_DOCUMENT_EDITING_STATUS:
    case SET_WHITEBOARD_INSTANCE:
        VideoLayout.resizeVideoArea();
        break;

    case TRACK_ADDED:
        if (action.track.mediaType === MEDIA_TYPE.SCREENSHARE) {
            // Handle screenshare tracks for both local and remote

            // For screenshare, the video element is managed by ScreenShareVideo
            // Just trigger sorting to update layout
            VideoLayout.sortThumbnails();
        } else if (!action.track.local && action.track.mediaType !== MEDIA_TYPE.AUDIO) {
            // Handle regular video tracks for remote participants
            VideoLayout.onRemoteStreamAdded(action.track.mhTrack);
            VideoLayout.sortThumbnails();
        }

        break;
    case TRACK_REMOVED:
        if (action.track.mediaType === MEDIA_TYPE.SCREENSHARE) {
            // Handle screenshare track removal for both local and remote

            VideoLayout.onRemoteStreamRemoved(action.track.mhTrack);
            VideoLayout.sortThumbnails();
        } else if (!action.track.local && action.track.mediaType !== MEDIA_TYPE.AUDIO) {
            // Handle regular video tracks for remote participants
            VideoLayout.onRemoteStreamRemoved(action.track.mhTrack);
            VideoLayout.sortThumbnails();
        }
        break;

    case TRACK_UPDATED:
        if (shouldSortForTrackUpdate(action.track)) {
            VideoLayout.sortThumbnails();
        }
        break;

    case REMOTE_VIDEO_ZOOM_CHANGED: {
        const smallVideo = VideoLayout.getSmallVideo(action.participantId);

        if (smallVideo && smallVideo.updateZoomTransform) {
            smallVideo.updateZoomTransform();
        } else if (VideoLayout.updateAllZoomTransforms) {
            VideoLayout.updateAllZoomTransforms();
        }
        break;
    }

    case LOCAL_VIDEO_ZOOM_CHANGED: {
        const localId = getLocalParticipant(store.getState())?.id;
        const localThumb = localId ? VideoLayout.getSmallVideo(localId) : null;

        if (localThumb && localThumb.updateZoomTransform) {
            localThumb.updateZoomTransform();
        } else if (VideoLayout.updateAllZoomTransforms) {
            VideoLayout.updateAllZoomTransforms();
        }
        break;
    }
    }

    return result;
});
